import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, ChevronDown, Download, FileArchive, FileText, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, Upload, X } from 'lucide-react';
import { deleteDiligenceTemplate, getDiligenceTemplateDownloadUrl, retryDiligenceTemplateProcessing, updateDiligenceTemplate, uploadDiligenceTemplate } from '../services/diligenceTemplateService';
import { useTemplateLibraryPages } from '../hooks/useTemplateLibraryPages';
import type { DiligenceTemplateSummary } from '../types';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import { startArtifactDownload } from '../utils/artifactDownload';

interface DiligenceTemplateLibraryProps { isOpen: boolean; onClose: () => void; }
const TEMPLATE_REFRESH_EVENT = 'juris:diligence-template-refresh';

const normalizeSearchText = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('es');

const TemplateStatus: React.FC<{ template: DiligenceTemplateSummary }> = ({ template }) => {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(false);
  if (template.usability === 'usable') return null;
  const isProcessing = template.usability === 'processing';

  return (
    <div role={isProcessing ? 'status' : 'alert'} aria-live={isProcessing ? 'polite' : 'assertive'} className={`mt-3 rounded-xl border px-3 py-2 ${isProcessing ? 'border-info/40 bg-info-soft text-info' : 'border-danger bg-danger-soft text-danger'}`}>
      <div className="flex items-center gap-2 text-xs font-extrabold">
        {isProcessing ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <AlertTriangle size={15} aria-hidden="true" />}
        <span>{isProcessing ? 'Preparando plantilla' : 'Archivo incompatible'}</span>
      </div>
      {!isProcessing && (
        <p className="mt-1 text-xs leading-relaxed opacity-90">{template.statusReason?.trim() || 'Juris no puede trabajar con este archivo de forma segura. Sube una versión nueva para sustituirlo.'}</p>
      )}
      {template.usability === 'incompatible' && (
        <button
          type="button"
          disabled={retrying}
          onClick={async () => {
            setRetrying(true);
            setRetryError(false);
            try {
              await retryDiligenceTemplateProcessing(template.id);
              [300, 2500, 6000].forEach((delay) => window.setTimeout(() => (
                window.dispatchEvent(new Event(TEMPLATE_REFRESH_EVENT))
              ), delay));
              window.setTimeout(() => setRetrying(false), 6500);
            } catch {
              setRetryError(true);
              setRetrying(false);
            }
          }}
          className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-xl border border-danger bg-panel px-3 text-xs font-bold text-danger hover:bg-danger-soft disabled:opacity-50"
        >
          <RefreshCw size={15} className={retrying ? 'animate-spin' : undefined} aria-hidden="true" />
          {retrying ? 'Reintentando…' : 'Reintentar preparación'}
        </button>
      )}
      {retryError && <p role="alert" className="mt-1 text-xs font-semibold">No se pudo iniciar el reintento.</p>}
    </div>
  );
};

const DiligenceTemplateLibrary: React.FC<DiligenceTemplateLibraryProps> = ({ isOpen, onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const deleteDialogRef = useRef<HTMLDivElement>(null);
  const cancelDeleteButtonRef = useRef<HTMLButtonElement>(null);
  const deleteTriggerRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addTemplateButtonRef = useRef<HTMLButtonElement>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);
  const versionTargetRef = useRef<DiligenceTemplateSummary | null>(null);
  const recoveredProcessingRef = useRef(new Set<string>());
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [userInstructions, setUserInstructions] = useState('');
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [templatePendingDelete, setTemplatePendingDelete] = useState<DiligenceTemplateSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { templates, loading, loadingMore, pageError, hasMore, loadMore } = useTemplateLibraryPages(isOpen, refreshKey);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editInstructions, setEditInstructions] = useState('');
  useModalAccessibility(dialogRef, {
    enabled: isOpen && !templatePendingDelete,
    onClose: saving || deleting ? undefined : onClose,
    initialFocusRef: deleteTriggerRef
  });
  useModalAccessibility(deleteDialogRef, {
    enabled: isOpen && Boolean(templatePendingDelete),
    onClose: deleting ? undefined : () => {
      setTemplatePendingDelete(null);
      setDeleteError(null);
    },
    initialFocusRef: cancelDeleteButtonRef
  });

  useEffect(() => {
    if (!isOpen) {
      setTemplatePendingDelete(null);
      setDeleteError(null);
      setSearchQuery('');
      setAddingTemplate(false);
      setFile(null);
      setDisplayName('');
      setDescription('');
      setUserInstructions('');
      setEditingId(null);
      setEditName('');
      setEditDescription('');
      setEditInstructions('');
      versionTargetRef.current = null;
      deleteTriggerRef.current = null;
    }
    setError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const refresh = () => setRefreshKey((value) => value + 1);
    window.addEventListener(TEMPLATE_REFRESH_EVENT, refresh);
    return () => window.removeEventListener(TEMPLATE_REFRESH_EVENT, refresh);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || saving || loading || loadingMore || !templates.some((template) => template.usability === 'processing')) return;
    const refreshTimer = window.setTimeout(() => setRefreshKey((value) => value + 1), 3000);
    return () => window.clearTimeout(refreshTimer);
  }, [isOpen, saving, loading, loadingMore, templates]);

  useEffect(() => {
    if (!isOpen || saving) return;
    const stale = templates.filter((template) => {
      const recoveryKey = `${template.id}:${template.updatedAt || template.processingStartedAt || ''}`;
      if (template.usability !== 'processing' || !template.processingStartedAt
        || recoveredProcessingRef.current.has(recoveryKey)) return false;
      const startedAt = Date.parse(template.processingStartedAt);
      return Number.isFinite(startedAt) && Date.now() - startedAt > 6 * 60 * 1000;
    });
    if (stale.length === 0) return;
    stale.forEach((template) => recoveredProcessingRef.current.add(
      `${template.id}:${template.updatedAt || template.processingStartedAt || ''}`
    ));
    void Promise.allSettled(stale.map((template) => retryDiligenceTemplateProcessing(template.id)))
      .then(() => setRefreshKey((value) => value + 1));
  }, [isOpen, saving, templates]);

  if (!isOpen) return null;

  const normalizedQuery = normalizeSearchText(searchQuery.trim());
  const filteredTemplates = normalizedQuery
    ? templates.filter((template) => normalizeSearchText(template.name).includes(normalizedQuery))
    : templates;

  const closeUploadForm = () => {
    setFile(null); setDisplayName(''); setDescription(''); setUserInstructions('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setAddingTemplate(false);
    window.requestAnimationFrame(() => addTemplateButtonRef.current?.focus());
  };

  const handleUpload = async () => {
    if (!file || !displayName.trim() || !description.trim()) return;
    setSaving(true); setError(null);
    try {
      await uploadDiligenceTemplate(file, displayName.trim(), description.trim(), undefined, userInstructions.trim());
      closeUploadForm();
      setRefreshKey((value) => value + 1);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'No se pudo guardar la plantilla.');
    } finally { setSaving(false); }
  };

  const saveEdit = async (templateId: string) => {
    if (saving || !editName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await updateDiligenceTemplate(templateId, editName.trim(), editDescription.trim(), editInstructions.trim());
      setEditingId(null);
      setRefreshKey((value) => value + 1);
    } catch { setError('No se pudo actualizar la plantilla.'); }
    finally { setSaving(false); }
  };

  const handleVersionSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null;
    const target = versionTargetRef.current;
    event.target.value = '';
    if (!nextFile || !target) return;

    setSaving(true);
    setError(null);
    try {
      await uploadDiligenceTemplate(nextFile, target.name, target.description, target.id, target.userInstructions);
      setRefreshKey((value) => value + 1);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'No se pudo guardar la nueva versión.');
    } finally {
      versionTargetRef.current = null;
      setSaving(false);
    }
  };

  const requestTemplateDeletion = (template: DiligenceTemplateSummary, trigger: HTMLButtonElement) => {
    deleteTriggerRef.current = trigger;
    setTemplatePendingDelete(template);
    setDeleteError(null);
  };

  const handleDownload = async (templateId: string) => {
    setDownloadingId(templateId);
    setError(null);
    try {
      startArtifactDownload(await getDiligenceTemplateDownloadUrl(templateId));
    } catch {
      setError('No se pudo descargar el documento original. Inténtalo de nuevo.');
    } finally {
      setDownloadingId(null);
    }
  };

  const cancelTemplateEditing = (trigger: HTMLButtonElement) => {
    const card = trigger.closest('article');
    setEditingId(null);
    window.requestAnimationFrame(() => card?.querySelector<HTMLButtonElement>('[data-template-edit]')?.focus());
  };

  const cancelTemplateDeletion = () => {
    if (deleting) return;
    setTemplatePendingDelete(null);
    setDeleteError(null);
  };

  const handleTemplateDeletion = async () => {
    if (!templatePendingDelete || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDiligenceTemplate(templatePendingDelete.id);
      deleteTriggerRef.current = null;
      setTemplatePendingDelete(null);
      setRefreshKey((value) => value + 1);
    } catch {
      setDeleteError('No se pudo eliminar la plantilla. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-canvas/85 p-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] backdrop-blur-sm sm:justify-end sm:pl-12" onMouseDown={() => { if (!saving && !deleting) onClose(); }}>
      <aside ref={dialogRef} role="dialog" aria-modal="true" aria-hidden={templatePendingDelete ? 'true' : undefined} aria-labelledby="template-library-title" tabIndex={-1} onMouseDown={(event) => event.stopPropagation()} className="flex h-[min(52rem,88dvh)] max-h-full w-full max-w-lg flex-col overflow-hidden rounded-[1.5rem] border border-line/80 bg-panel shadow-2xl motion-fade-through-fast sm:h-full sm:rounded-[2rem]">
        <header className="flex shrink-0 items-start justify-between border-b border-line px-5 py-5 sm:px-7">
          <div><p className="text-xs font-extrabold uppercase tracking-[.16em] text-juris">Juris</p><h2 id="template-library-title" className="mt-1 text-xl font-extrabold text-ink">Plantillas de diligencias</h2><p className="mt-1 text-sm text-muted">Juris elegirá por el nombre y la descripción sin leer todas las plantillas.</p></div>
          <button type="button" onClick={onClose} disabled={saving || deleting} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50" aria-label="Cerrar biblioteca"><X size={20} /></button>
        </header>
        <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto p-5 sm:p-7">
          <input ref={versionFileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleVersionSelect} />
          <section className="rounded-2xl border border-dashed border-line bg-juris-soft/60" aria-label="Añadir plantilla">
            <button
              ref={addTemplateButtonRef}
              type="button"
              aria-expanded={addingTemplate}
              aria-controls="diligence-template-upload-form"
              disabled={saving || deleting}
              onClick={() => setAddingTemplate((value) => !value)}
              className="flex min-h-11 w-full items-center gap-2 rounded-2xl px-4 py-3 text-left font-bold text-ink transition-colors hover:bg-juris-soft/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 disabled:opacity-50"
            >
              <Plus size={18} aria-hidden="true" />
              <span className="flex-1">Añadir plantilla</span>
              <ChevronDown size={18} aria-hidden="true" className={`shrink-0 transition-transform motion-reduce:transition-none ${addingTemplate ? 'rotate-180' : ''}`} />
            </button>
            <div id="diligence-template-upload-form" hidden={!addingTemplate} className="px-4 pb-4">
            <label htmlFor="diligence-template-file" className="mt-3 block text-xs font-bold text-ink">Documento</label>
            <input id="diligence-template-file" ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(event) => { const next = event.target.files?.[0] || null; setFile(next); if (next && !displayName) setDisplayName(next.name.replace(/\.[^.]+$/, '')); }} className="mt-1 block w-full rounded-xl text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris file:mr-3 file:min-h-11 file:rounded-xl file:border-0 file:bg-panel file:px-4 file:font-bold file:text-ink" />
            <label htmlFor="diligence-template-name" className="mt-3 block text-xs font-bold text-ink">Nombre</label>
            <input id="diligence-template-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Ej. Exposición de hechos" className="mt-1 w-full rounded-xl border border-line bg-panel px-3 py-2.5 text-base outline-none focus:border-juris focus-visible:ring-2 focus-visible:ring-juris sm:text-sm" />
            <label htmlFor="diligence-template-description" className="mt-3 block text-xs font-bold text-ink">Cuándo debe usarla Juris</label>
            <textarea id="diligence-template-description" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Ej. Cuando se solicite una exposición de hechos" rows={2} className="mt-1 w-full resize-none rounded-xl border border-line bg-panel px-3 py-2.5 text-base outline-none focus:border-juris focus-visible:ring-2 focus-visible:ring-juris sm:text-sm" />
            <label htmlFor="diligence-template-instructions" className="mt-3 block text-xs font-bold text-ink">Instrucciones <span className="font-normal text-muted">(opcional)</span></label>
            <textarea id="diligence-template-instructions" value={userInstructions} onChange={(event) => setUserInstructions(event.target.value)} placeholder="Indica cómo quieres que Juris trabaje con esta plantilla" rows={3} maxLength={8000} className="mt-1 w-full resize-y rounded-xl border border-line bg-panel px-3 py-2.5 text-base outline-none focus:border-juris focus-visible:ring-2 focus-visible:ring-juris sm:text-sm" />
            <button type="button" disabled={!file || !displayName.trim() || !description.trim() || saving} onClick={handleUpload} className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-action px-4 font-bold text-white transition hover:bg-action-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 disabled:opacity-50"><Upload size={17} /> {saving ? 'Guardando…' : 'Guardar plantilla'}</button>
            <button type="button" disabled={saving} onClick={closeUploadForm} className="mt-2 flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-bold text-ink transition hover:bg-juris-soft/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50">Cancelar</button>
            </div>
          </section>
          {(error || pageError) && <div role="alert" className="mt-4 flex gap-2 rounded-xl border border-danger bg-danger-soft p-3 text-sm text-danger"><AlertCircle size={18} className="shrink-0" />{error || pageError}</div>}
          <div className="relative mt-3 w-full">
            <Search size={18} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input id="diligence-template-search" type="search" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} aria-label="Buscar plantillas por nombre" placeholder="Buscar plantillas…" autoComplete="off" className="min-h-11 w-full min-w-0 rounded-xl border border-line bg-canvas py-2 pl-10 pr-3 text-base text-ink outline-none placeholder:text-muted focus:border-juris focus-visible:ring-2 focus-visible:ring-juris sm:text-sm" />
          </div>
          <section className="mt-3" aria-labelledby="saved-templates-title" aria-busy={loading}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 id="saved-templates-title" className="text-sm font-extrabold text-ink">Tus plantillas</h3>
              {!loading && templates.length > 0 ? <span aria-label={`${templates.length} plantillas cargadas${hasMore ? ', hay más disponibles' : ''}`} className="rounded-full bg-juris-soft px-2.5 py-1 text-xs font-bold text-ink">{templates.length}{hasMore ? '+' : ''}</span> : null}
            </div>
            <div className="space-y-3">
            {loading ? <div className="flex justify-center py-8 text-juris"><Loader2 className="animate-spin" /></div> : templates.length === 0 ? <div className="py-8 text-center text-sm text-muted"><FileArchive className="mx-auto mb-2 opacity-40" />Aún no has guardado plantillas.</div> : filteredTemplates.length === 0 ? <div role="status" className="py-8 text-center text-sm text-muted">No hay plantillas cuyo nombre coincida con «{searchQuery.trim()}».</div> : filteredTemplates.map((template) => (
              <article key={template.id} className="rounded-2xl border border-line bg-panel p-4 shadow-sm">
                <div className="flex items-start gap-3"><div className={`${editingId === template.id ? 'hidden sm:flex' : 'flex'} h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning`}><FileText size={19} /></div><div className="min-w-0 flex-1">{editingId === template.id ? <div className="space-y-2"><input value={editName} onChange={(event) => setEditName(event.target.value)} aria-label="Nombre de la plantilla" className="min-h-11 w-full rounded-lg border border-line px-3 py-2.5 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm" /><textarea value={editDescription} onChange={(event) => setEditDescription(event.target.value)} aria-label="Cuándo debe usarla Juris" placeholder="Cuándo debe usarla Juris" rows={2} className="w-full resize-none rounded-lg border border-line px-3 py-2.5 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris sm:px-2 sm:py-1.5 sm:text-xs" /><textarea value={editInstructions} onChange={(event) => setEditInstructions(event.target.value)} aria-label="Instrucciones personalizadas (opcional)" placeholder="Instrucciones personalizadas (opcional)" rows={3} maxLength={8000} className="w-full resize-y rounded-lg border border-line px-3 py-2.5 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris sm:px-2 sm:py-1.5 sm:text-xs" /></div> : <><div className="flex min-w-0 items-center gap-2"><h4 className="truncate font-bold text-ink">{template.name}</h4><span className="shrink-0 rounded-full bg-juris-soft px-2 py-1 text-[10px] font-extrabold uppercase tracking-wide text-ink">{template.sourceFormat}</span></div><p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted">{template.description || 'Sin descripción'}</p>{template.userInstructions.trim() && <p className="mt-2 line-clamp-3 whitespace-pre-line text-xs leading-relaxed text-muted"><span className="font-bold text-ink">Instrucciones:</span> {template.userInstructions}</p>}</>}<TemplateStatus template={template} /></div></div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-1 border-t border-line pt-2">
                  {editingId === template.id && <div className="flex w-full items-center justify-between gap-1 sm:mr-auto sm:w-auto"><button type="button" disabled={saving || deleting} onClick={() => { versionTargetRef.current = template; versionFileInputRef.current?.click(); }} className="inline-flex min-h-11 items-center gap-2 rounded-xl px-2 text-xs font-bold text-ink transition hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50" aria-label={`Cambiar documento de ${template.name}`}><Upload size={15} aria-hidden="true" />Cambiar documento</button><button type="button" disabled={saving || deleting} onClick={(event) => cancelTemplateEditing(event.currentTarget)} className="min-h-11 rounded-xl px-2 text-xs font-bold text-muted transition hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50" aria-label={`Cancelar edición de ${template.name}`}>Cancelar</button></div>}
                  <div className="ml-auto flex shrink-0 gap-1">
                  <button type="button" disabled={saving || deleting || downloadingId !== null} onClick={() => void handleDownload(template.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-muted transition hover:bg-juris-soft hover:text-juris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50" title="Descargar documento original" aria-label={`Descargar original de ${template.name}`}>{downloadingId === template.id ? <Loader2 size={17} className="animate-spin" aria-hidden="true" /> : <Download size={17} aria-hidden="true" />}</button>
                  <button data-template-edit type="button" disabled={deleting || saving || (editingId === template.id && !editName.trim())} onClick={() => { if (editingId === template.id) void saveEdit(template.id); else { setEditingId(template.id); setEditName(template.name); setEditDescription(template.description); setEditInstructions(template.userInstructions); } }} className="flex h-11 w-11 items-center justify-center rounded-full text-muted transition hover:bg-juris-soft hover:text-juris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50" aria-label={editingId === template.id ? `Guardar ${template.name}` : `Editar ${template.name}`}>{editingId === template.id ? <Check size={17} /> : <Pencil size={17} />}</button>
                  <button type="button" disabled={saving || deleting} onClick={(event) => requestTemplateDeletion(template, event.currentTarget)} className="flex h-11 w-11 items-center justify-center rounded-full text-muted transition hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger disabled:opacity-50" aria-label={`Eliminar ${template.name}`}><Trash2 size={17} /></button>
                  </div>
                </div>
              </article>
            ))}
            </div>
            {!loading && hasMore && <div className="mt-4 text-center">
              {searchQuery.trim() && <p className="mb-2 text-xs text-muted">La búsqueda filtra las plantillas cargadas. Carga más para ampliarla.</p>}
              <button type="button" disabled={loadingMore || saving || deleting} onClick={() => void loadMore()} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-line bg-raised px-5 text-sm font-bold text-ink transition hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris disabled:opacity-50">{loadingMore && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}{loadingMore ? 'Cargando…' : 'Cargar más'}</button>
            </div>}
          </section>
        </div>
      </aside>
    </div>
    {templatePendingDelete && (
      <div className="fixed inset-0 z-[120] flex items-end justify-center bg-canvas/85 p-4 backdrop-blur-sm sm:items-center" onMouseDown={cancelTemplateDeletion}>
        <div
          ref={deleteDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-title"
          aria-describedby="delete-template-description"
          tabIndex={-1}
          onMouseDown={(event) => event.stopPropagation()}
          className="w-full max-w-sm rounded-[1.75rem] border border-line/70 bg-panel p-6 shadow-2xl motion-micro-scale"
        >
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger"><Trash2 size={22} aria-hidden="true" /></div>
          <h2 id="delete-template-title" className="text-xl font-extrabold text-ink">Eliminar plantilla</h2>
          <p id="delete-template-description" className="mt-2 text-sm leading-relaxed text-muted">Se eliminarán <strong>{templatePendingDelete.name}</strong> y todas sus versiones. Esta acción no se puede deshacer.</p>
          {deleteError && <div role="alert" className="mt-4 flex gap-2 rounded-xl border border-danger bg-danger-soft p-3 text-sm text-danger"><AlertCircle size={18} className="shrink-0" />{deleteError}</div>}
          <div className="mt-6 flex gap-3">
            <button ref={cancelDeleteButtonRef} type="button" onClick={cancelTemplateDeletion} disabled={deleting} className="min-h-11 flex-1 rounded-xl bg-raised px-4 font-bold text-ink transition hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 disabled:opacity-50">Cancelar</button>
            <button type="button" onClick={() => void handleTemplateDeletion()} disabled={deleting} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-danger-action px-4 font-bold text-white transition hover:bg-danger-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger focus-visible:ring-offset-2 disabled:opacity-60">{deleting ? <><Loader2 size={17} className="animate-spin" aria-hidden="true" /> Eliminando…</> : 'Eliminar'}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default DiligenceTemplateLibrary;
